import * as THREE from "three";
import { Spatial } from "../geo/Spatial";
import { GeoCoords } from "../geo/GeoCoords";
import { JumpCalculatorCoefficients } from "./JumpCalculatorCoefficients";
import { JumpMode } from "./JumpMode";
import proj4 from "proj4";
export class JumpNodeCalculator {
    constructor(jumpmode = JumpMode.similar) {
        this._geoCoords = new GeoCoords();
        this._coefficients = new JumpCalculatorCoefficients(jumpmode);
    }
    setJumpMode(mode) {
        this._coefficients = new JumpCalculatorCoefficients(mode);
    }
    getPotentialJumpNodes(potentialNodes, reference_node = null, reference_position = null) {
        const potentialJumpNodes = [];
        for (const potentialNode of potentialNodes) {
            let sameProject = false;
            let sameSensorgroup = potentialNode.frame.capturesystem.sensorgroup === 3;
            let orientationDifference = Spatial.angleDifference((potentialNode.frame.capturesystem.orientation * Math.PI) / 180, 0);
            let directionChange = 0;
            let sameAxisDirection = false;
            if (reference_node) {
                sameProject =
                    reference_node.projectKey != null &&
                        potentialNode.projectKey != null &&
                        potentialNode.projectKey === reference_node.projectKey;
                sameSensorgroup =
                    potentialNode.frame.capturesystem.sensorgroup === reference_node.frame.capturesystem.sensorgroup;
                orientationDifference = Spatial.angleDifference((potentialNode.frame.capturesystem.orientation * Math.PI) / 180, (reference_node.frame.capturesystem.orientation * Math.PI) / 180);
                const currentDirection = Spatial.viewingDirection(reference_node.rotation);
                const direction = Spatial.viewingDirection(potentialNode.rotation);
                directionChange = Spatial.angleBetweenVector2(currentDirection.x, currentDirection.y, direction.x, direction.y);
                directionChange = Math.abs(directionChange);
                const axis = this._scene.getLRHandler().getActiveNavigationAxis();
                if (axis) {
                    const ref_easting_northing = proj4("EPSG:4326", `EPSG:${this._scene.viewer.epsg || reference_node.frame.epsg}`, {
                        x: reference_node.frame.longitude,
                        y: reference_node.frame.latitude,
                        z: reference_node.frame.height,
                    });
                    const ref_curr_uvw = axis.xyz2uvw(ref_easting_northing.x, ref_easting_northing.y, reference_node.frame.height);
                    if (ref_curr_uvw) {
                        const ref_curr_absU = axis.u2absU(ref_curr_uvw.featureidx, ref_curr_uvw.lrcoords[0]);
                        const ref_azi = this._getHorizontalOrientation(reference_node.frame.omega, reference_node.frame.phi);
                        const ref_future_easting = ref_easting_northing.x + 5 * Math.sin((ref_azi * Math.PI) / 180);
                        const ref_future_northing = ref_easting_northing.y + 5 * Math.cos((ref_azi * Math.PI) / 180);
                        const ref_future_uvw = axis.xyz2uvw(ref_future_easting, ref_future_northing, reference_node.frame.height);
                        if (ref_future_uvw) {
                            const ref_future_absU = axis.u2absU(ref_future_uvw.featureidx, ref_future_uvw.lrcoords[0]);
                            const pot_easting_northing = proj4("EPSG:4326", `EPSG:${this._scene.viewer.epsg || potentialNode.frame.epsg}`, {
                                x: potentialNode.frame.longitude,
                                y: potentialNode.frame.latitude,
                                z: potentialNode.frame.height,
                            });
                            const pot_curr_uvw = axis.xyz2uvw(pot_easting_northing.x, pot_easting_northing.y, potentialNode.frame.height);
                            if (pot_curr_uvw) {
                                const pot_curr_absU = axis.u2absU(pot_curr_uvw.featureidx, pot_curr_uvw.lrcoords[0]);
                                const pot_azi = this._getHorizontalOrientation(potentialNode.frame.omega, potentialNode.frame.phi);
                                const pot_future_easting = pot_easting_northing.x + 5 * Math.sin((pot_azi * Math.PI) / 180);
                                const pot_future_northing = pot_easting_northing.y + 5 * Math.cos((pot_azi * Math.PI) / 180);
                                const pot_future_uvw = axis.xyz2uvw(pot_future_easting, pot_future_northing, potentialNode.frame.height);
                                if (pot_future_uvw) {
                                    const pot_future_absU = axis.u2absU(pot_future_uvw.featureidx, pot_future_uvw.lrcoords[0]);
                                    if ((ref_curr_absU - ref_future_absU > 0 && pot_curr_absU - pot_future_absU > 0) ||
                                        (ref_curr_absU - ref_future_absU < 0 && pot_curr_absU - pot_future_absU < 0)) {
                                        sameAxisDirection = true;
                                    }
                                }
                            }
                        }
                    }
                }
            }
            let distance = 0;
            if (reference_position) {
                const enu = this._geoCoords.geodeticToEnu(potentialNode.latLon.lat, potentialNode.latLon.lon, potentialNode.alt, reference_position.lat, reference_position.lon, potentialNode.alt);
                const motion = new THREE.Vector3(enu[0], enu[1], enu[2]);
                distance = motion.length();
            }
            const today = new Date();
            const timestamp = new Date(potentialNode.frame.timestamp);
            const timediff = Math.round((today.getTime() - timestamp.getTime()) / (24 * 3600 * 1000));
            const potentialJump = {
                sameProject: sameProject,
                sameSensorgroup: sameSensorgroup,
                orientationDifference: orientationDifference,
                distance: distance,
                key: potentialNode.key,
                timediffDays: timediff,
                sameAxisDirection: sameAxisDirection,
            };
            potentialJumpNodes.push(potentialJump);
        }
        return potentialJumpNodes;
    }
    computeSimilarNode(potentialJumpNodes, force_similar = false) {
        let jump_key;
        let lowestScore = Number.MAX_VALUE;
        const timeDiffPenalty = !force_similar ? this._coefficients.timeDiff : 0;
        for (const potential of potentialJumpNodes) {
            const force_similar_for_distances_over20m = potential.distance > 20 ? 10000 : 1;
            const score = this._coefficients.similarProjectPenalty * (potential.sameProject ? 0 : 1) +
                this._coefficients.similarSensorgroupPenalty * (potential.sameSensorgroup ? 0 : 1) +
                (this._coefficients.similarOrientation * Math.abs(potential.orientationDifference)) / (Math.PI / 3) +
                ((timeDiffPenalty + 1) * (this._coefficients.similarDistance * potential.distance)) / 40 +
                timeDiffPenalty * force_similar_for_distances_over20m * potential.timediffDays +
                this._coefficients.similarAxisDirectionPenalty * (potential.sameAxisDirection ? 0 : 1);
            (this._coefficients.similarDirection * potential.orientationDifference) / Math.PI;
            if (score < lowestScore) {
                lowestScore = score;
                jump_key = potential.key;
            }
        }
        return jump_key;
    }
    setScene(scene) {
        this._scene = scene;
    }
    _getHorizontalOrientation(omega, phi) {
        omega = (omega * Math.PI) / 180;
        phi = (phi * Math.PI) / 180;
        const x = -1 * Math.sin(phi);
        const y = Math.sin(omega) * Math.cos(phi);
        let azi = 0;
        if (x > 0 && y > 0) {
            azi = (Math.atan(x / y) * 180) / Math.PI;
        }
        else if (x > 0 && y < 0) {
            azi = ((Math.atan(x / y) + Math.PI) * 180) / Math.PI;
        }
        else if (x < 0 && y < 0) {
            azi = ((Math.atan(x / y) + Math.PI) * 180) / Math.PI;
        }
        else if (x < 0 && y > 0) {
            azi = ((Math.atan(x / y) + 2 * Math.PI) * 180) / Math.PI;
        }
        return azi;
    }
}
//# sourceMappingURL=JumpNodeCalculator.js.map